<template>
  <UIPopup
    v-model="modelValue"
    closeable
    hide-buttons
    :max-width="isDesktop ? '768px' : isTablet ? '584px' : ''"
    @close="modelValue = false"
  >
    <template #header>
      <div class="mx-auto max-w-[468px] text-black-80" :class="step === 2 || !step ? 'mt-24' : 'mt-10'">
        <UIBaseTransition>
          <div v-if="step === 2">
            <h1 class="text-center text-2xl">{{ $t('support.contactAgent.steps.success.title') }}</h1>
          </div>
          <div v-if="step === 1">
            <h1 class="text-center text-2xl">{{ $t('support.contactAgent.steps.method.title') }}</h1>
          </div>
          <div v-else>
            <h1 class="text-2xl font-bold tablet:text-left">{{ $t('support.contactAgent.steps.email.title') }}</h1>
          </div>
        </UIBaseTransition>
      </div>
    </template>
    <template #body>
      <UIBaseTransition>
        <div v-if="step === 2" class="mx-auto flex max-w-[361px] flex-col items-center justify-center gap-8">
          <span class="text-lg">{{ $t('support.contactAgent.steps.success.message') }}</span>
          <UIIcon name="handshake-filled" size="unset" class="!size-[256px]" />
        </div>
        <div v-else-if="!step" class="mx-auto flex max-w-[468px] flex-col gap-4 tablet:text-left">
          <span class="text-base font-normal !leading-4">
            {{ $t('support.contactAgent.steps.email.description') }}
          </span>
          <form ref="parentRef" @submit.prevent="onSubmit">
            <div class="flex w-full flex-col gap-4">
              <UIInputText name="subject" :placeholder="$t('support.contactAgent.steps.email.subjectPlaceholder')" />
              <UITextArea
                name="message"
                :placeholder="$t('support.contactAgent.steps.email.messagePlaceholder')"
                min-height="100"
                sync-v-model
              />
              <UIButton id="submit" full-width type="submit">
                {{ $t('support.contactAgent.steps.email.send') }}
              </UIButton>
            </div>
          </form>
        </div>
        <div v-else-if="step === 1" class="mx-auto flex w-full max-w-[386px] flex-col gap-4">
          <UIButton id="whatsapp" variant="success" full-width left-icon="whatsapp" @click="sendWhatsAppMessage">
            {{ $t('support.contactAgent.steps.method.whatsapp') }}
          </UIButton>
          <UIButton id="email" :loading="isPending" variant="secondary" full-width left-icon="email" @click="sendEmail">
            {{ $t('support.contactAgent.steps.method.email') }}
          </UIButton>
        </div>
      </UIBaseTransition>
    </template>
  </UIPopup>
</template>

<script setup lang="ts">
import { useForm } from 'vee-validate'
import { contactSchema } from '@/schemas/support/contact'

const modelValue = defineModel<boolean>()

const authStore = useAuthStore()

const { isTablet, isDesktop } = useAppBreakpoints()

const { handleSubmit } = useForm<{ name: string; subject: string; message: string }>({
  validationSchema: contactSchema,
})

const { catchErrors } = useErrorHandler()

const { mutateAsync: sendMessageToAgent, isPending } = useContactAgent()

const step = ref(0)
const formValues = ref({ name: '', subject: '', message: '' })

const onSubmit = handleSubmit((values) => {
  step.value = 1
  formValues.value = values
})

const sendEmail = () => {
  sendMessageToAgent({ ...formValues.value, agentEmail: authStore.getAgent?.email })
    .then(() => {
      step.value = 2
    })
    .catch(catchErrors)
}

const sendWhatsAppMessage = () => {
  const agentPhone = authStore.getAgent?.phone

  if (agentPhone) {
    const formattedPhone = agentPhone.replace(/\D/g, '') // Remove all non-digit characters
    const encodedMessage = encodeURIComponent(formValues.value.message) // URL-encode the message text
    window.open(`https://wa.me/${formattedPhone}?text=${encodedMessage}`, '_blank')
  }
}

const [parentRef] = useAutoAnimate()
</script>
